'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useDeferredValue, useEffect, useRef, useState, useTransition } from 'react';

import { HydrationBoundary, QueryClient, dehydrate, useSuspenseQuery } from '@tanstack/react-query';
import cx from 'classnames';
import { useInView } from 'motion/react';

import type { SearchRecord } from '~/ingest/search/model/searchRecord';
import { Filters } from '~/ui/components/filters';
import { Column, Grid } from '~/ui/components/grid';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Icon } from '~/ui/components/icon';
import { Suggestions as BaseSuggestions } from '~/ui/components/search/suggestions/suggestions';
import navigationStyleConstants from '~/ui/modules/layout/navigation/navigation.constants.module.scss';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { type Filter, getSelectedFilterOptions } from '~/ui/systems/filter';
import { type Route } from '~/ui/systems/internalLink/route';
import { convertRemToPixels } from '~/ui/utils/convertRemToPixels';
import { SearchSuggestionSkeleton } from '~/v1/components/search/suggestions/suggestion/suggestion.skeleton';

import styles from './search.module.scss';

const QUERY_SEARCH_PARAM = 'query';

export interface SearchProps {
  className?: string;
  endpoint: Route;
  query?: string;
  inputLabel?: string;
  eyebrow?: string;
  renderEyebrow?: boolean;
  filters?: Filter[];
  loadingSkeleton?: React.ReactNode;
  hasResults?: boolean;
  hideSearchBar?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

export function ClientSearch({
  className,
  endpoint,
  query: initialQuery,
  inputLabel,
  eyebrow,
  renderEyebrow,
  filters,
  loadingSkeleton,
  hasResults,
  hideSearchBar,
  fullWidth,
  children,
}: SearchProps): React.ReactElement {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoadingNewPage, startLoadingNewPageTransition] = useTransition();
  const [query, setQuery] = useState(initialQuery ?? '');
  const [hasChanged, setHasChanged] = useState(false);
  const deferredQuery = useDeferredValue(query);
  const bp = useBreakpoint();
  const searchBarRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<HTMLDivElement>(null);
  // This accounts for the height of the nav bar on mobile and tablet
  const [searchBarMargin, setSearchBarMargin] = useState<number>(0);
  const searchBarInView = useInView(searchBarRef, {
    margin: `${searchBarMargin}px 0px 0px 0px`,
  });
  const filterBarInView = useInView(filterBarRef);

  const scrollOnSubmit = !searchBarInView && !filterBarInView;

  const filterApplied =
    !!filters &&
    filters.filter(filter => filter.includeInPills).flatMap(getSelectedFilterOptions).length > 0;
  // (hasResults || filterApplied)
  // In the case where filters were applied and produced no results,
  // render the component so that the user can still interact with it.
  //
  // (!hasChanged || query.length < 4)
  // Results will still be displayed when no suggestions are being displayed.
  // In this case, the component should still be rendered.
  const displayFilters = (hasResults || filterApplied) && (!hasChanged || query.length < 4);
  const isSticky = !searchBarInView && displayFilters;

  useEffect(() => {
    setSearchBarMargin(
      bp === Breakpoint.MOBILE
        ? -1 * convertRemToPixels(navigationStyleConstants.primaryStickyMobileHeight)
        : bp === Breakpoint.TABLET
          ? -1 * convertRemToPixels(navigationStyleConstants.primaryStickyTabletHeight)
          : 0,
    );
  }, [bp]);

  useEffect(() => {
    // if initialQuery changes, it would have to be a route change
    setQuery(initialQuery ?? '');
  }, [initialQuery]);

  // Whenever filters change, the List component that drives the ability to load
  //  more results doesn't properly re-render in time. This caused it to retain
  //  stale search results with the previous query until it was invoked again.
  //  Somehow, triggering the transition fixes the issue. searchParams is used
  //  as the dependency since it is guaranteed to change when filters change.
  useEffect(() => {
    startLoadingNewPageTransition(() => {});
  }, [searchParams]);

  let content: React.ReactNode;
  if (isLoadingNewPage && loadingSkeleton) {
    content = (
      <div className={styles.contentSkeleton}>
        {Array.from({ length: 50 }).map((_, index) => (
          <div key={index}>{loadingSkeleton}</div>
        ))}
      </div>
    );
  } else if (isLoadingNewPage || (hasChanged && query.length >= 4)) {
    content = (
      <Suspense
        fallback={
          <Grid fullWidth={fullWidth}>
            <Column
              span={{
                [Breakpoint.MOBILE]: fullWidth ? 7 : 5,
                [Breakpoint.TABLET]: fullWidth ? 9 : 7,
                [Breakpoint.DESKTOP]: fullWidth ? 9 : 7,
              }}
              offsetLeft={{
                ...(!fullWidth && { [Breakpoint.DESKTOP]: 1 }),
              }}
            >
              <SearchSuggestionSkeleton />
            </Column>
          </Grid>
        }
      >
        <Suggestions endpoint={endpoint} query={deferredQuery} fullWidth={fullWidth} />
      </Suspense>
    );
  } else if (!!children) {
    content = children;
  }

  return (
    <>
      <div
        className={cx(styles.searchBar, className, {
          [styles.isSticky]: isSticky,
          [styles.hidden]: hideSearchBar,
        })}
        aria-hidden={isSticky}
      >
        <Grid
          className={cx({
            [styles.searchBarGrid]: !fullWidth,
          })}
          fullWidth={fullWidth}
        >
          <Column
            span={{
              [Breakpoint.MOBILE]: fullWidth ? 7 : 5,
              [Breakpoint.TABLET]: fullWidth ? 9 : 7,
              [Breakpoint.DESKTOP]: fullWidth ? 9 : 7,
            }}
            offsetLeft={{
              ...(!fullWidth && { [Breakpoint.DESKTOP]: 1 }),
            }}
          >
            {renderEyebrow && (
              <div className={styles.eyebrowContainer}>
                {isLoadingNewPage ? (
                  <div className={styles.eyebrowSkeleton} />
                ) : !eyebrow || (hasChanged && query.length >= 4) || !query.length ? (
                  <></>
                ) : (
                  <label className={cx(styles.eyebrow, typographyStyles.paragraph)}>
                    {eyebrow}
                  </label>
                )}
              </div>
            )}

            <HydrationBoundary state={dehydrate(new QueryClient())}>
              <form
                action={endpoint}
                onChange={event => {
                  const url = getActionUrl(event, endpoint);
                  // TODO: can we prefetch when filters are modified? (before submitting)
                  router.prefetch(url);
                }}
                onSubmit={event => {
                  event.preventDefault();
                  setHasChanged(false);

                  // The URL query string should contain "query" at this point,
                  // so it should be deleted to prevent duplicate entries
                  const url = getActionUrl(event, endpoint);
                  const newSearchParams = new URLSearchParams(searchParams?.toString());
                  newSearchParams.delete(QUERY_SEARCH_PARAM);
                  startLoadingNewPageTransition(() => {
                    router.push(`${url}${newSearchParams.size ? `&${newSearchParams}` : ''}`, {
                      scroll: scrollOnSubmit,
                    });
                  });
                }}
                onReset={event => {
                  setHasChanged(false);
                  setQuery('');
                  event.currentTarget.querySelector('input')?.focus();
                }}
                className={styles.form}
              >
                <input
                  name={QUERY_SEARCH_PARAM}
                  value={query}
                  placeholder={inputLabel ? inputLabel : 'Search'}
                  onChange={event => {
                    setHasChanged(true);
                    setQuery(event.target.value);
                  }}
                  className={cx(styles.input, typographyStyles.pageTitleSmall)}
                />
                <button type={hasChanged ? 'submit' : 'reset'} className={styles.button}>
                  {query ? (
                    hasChanged ? (
                      <Icon name="arrow-right" />
                    ) : (
                      <Icon name="close" />
                    )
                  ) : (
                    <Icon name="search" />
                  )}
                </button>
                <div className={styles.border} />
              </form>
            </HydrationBoundary>
          </Column>
        </Grid>
      </div>

      <div ref={searchBarRef} />

      {filters && displayFilters ? (
        <Filters
          query={initialQuery}
          eyebrow={eyebrow}
          title="Sort & Filter"
          filters={filters}
          isSticky={isSticky}
          scrollOnSubmit={scrollOnSubmit}
        />
      ) : null}

      {content}
    </>
  );
}

function Suggestions({
  endpoint,
  query,
  fullWidth,
}: {
  endpoint: Route;
  query: string;
  fullWidth?: boolean;
}) {
  const { data } = useSuspenseQuery({
    queryKey: ['/api/search/suggestions', query],
    queryFn: async ({ queryKey, signal }) => {
      const url = queryKey[0] + `?endpoint=${endpoint}&${QUERY_SEARCH_PARAM}=${queryKey[1]}`;
      const res = await fetch(url, {
        signal,
      });
      const json = await res.json();
      return json as {
        queryId: string | undefined;
        suggestions: Record<string, SearchRecord[]>;
      };
    },
    staleTime: 60 * 1000,
  });

  return (
    <div className={styles.suggestions}>
      {!Object.keys(data.suggestions).length ? (
        <Grid fullWidth={fullWidth}>
          <Column
            span={{
              [Breakpoint.MOBILE]: fullWidth ? 7 : 5,
              [Breakpoint.TABLET]: fullWidth ? 9 : 7,
              [Breakpoint.DESKTOP]: fullWidth ? 9 : 7,
            }}
            offsetLeft={{
              ...(!fullWidth && { [Breakpoint.DESKTOP]: 1 }),
            }}
          >
            <p className={cx(styles.noSuggestionsLabel, typographyStyles.bodySmall)}>
              No suggested searches found for &lsquo;{query}&rsquo;
            </p>
          </Column>
        </Grid>
      ) : (
        <>
          {Object.entries(data.suggestions).map(([label, hits], index) => (
            <BaseSuggestions key={index} label={label} suggestions={hits} fullWidth={fullWidth} />
          ))}
        </>
      )}
    </div>
  );
}

function getActionUrl(event: React.FormEvent<HTMLFormElement>, action: string) {
  let targetUrl: URL;
  try {
    targetUrl = new URL(action, document.baseURI);
  } catch (error) {
    throw new Error(`Cannot parse form action '${action}' as a URL`, {
      cause: error,
    });
  }

  if (targetUrl.searchParams.size) {
    // url-encoded HTML forms *overwrite* any search params in the `action` url:
    //
    //  "Let `query` be the result of running the application/x-www-form-urlencoded serializer [...]"
    //  "Set parsed action's query component to `query`."
    //   https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#submit-mutate-action
    //
    // We need to match that.
    // (note that all other parts of the URL, like `hash`, are preserved)
    targetUrl.search = '';
  }

  const formData = new FormData(event.currentTarget);
  for (const [name, value] of formData) {
    if (typeof value !== 'string') {
      throw new Error(`Unexpected form value for '${name}': ${value}`);
    }

    targetUrl.searchParams.append(name, value);
  }

  return targetUrl.pathname + targetUrl.search;
}
